import { useCallback } from "react";

import { useFormikWithoutDirtyForm } from "@shared";
import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";

import { AttendanceEntryRequest, AttendanceType, PurposeType, appointmentsApi } from "@/api";
import { PlanWeeklyAttendanceSeriesRequest, PlanWeeklySeriesRequest } from "@/api/api-client";
import {
  AttendanceEntryFormValues,
  WeeklySeriesDayFormValues,
  WeeklySeriesFormValues,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.types";
import {
  WEEKDAYS,
  createEmptyEntry,
  dateToDayOfWeek,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.utils";
import { weeklySeriesValidationSchema } from "@/common/components/appointments/plan-attendance/components/weekly-series-form/WeeklySeriesForm.utils";
import { useAttendancePurposes } from "@/common/components/appointments/plan-attendance/hooks/useAttendancePurposes";
import { deriveAvailability } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { AllDayEndHour, AllDayStartHour } from "@/common/components/appointments/shared/appointmentConstants";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { SystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

function buildInitialEmptyDays(): WeeklySeriesDayFormValues[] {
  return WEEKDAYS.map((weekday): WeeklySeriesDayFormValues => ({ weekday, entries: [createEmptyEntry()] }));
}

function buildSeriesEntries(
  days: WeeklySeriesDayFormValues[],
  purposes: SystemDropdownOptions
): AttendanceEntryRequest[] {
  return days.flatMap((day) =>
    day.entries.flatMap((entry): AttendanceEntryRequest[] => {
      const isAbsent = entry.attendanceType === AttendanceType.Absent;
      if (!entry.purposeKey || entry.attendanceType === null || (!isAbsent && (!entry.startTime || !entry.endTime))) {
        return [];
      }
      const startTime =
        isAbsent || entry.startTime == null
          ? new Date(new Date(0).setHours(AllDayStartHour, 0, 0, 0))
          : entry.startTime;
      const endTime =
        isAbsent || entry.endTime == null ? new Date(new Date(0).setHours(AllDayEndHour, 0, 0, 0)) : entry.endTime;
      return [
        {
          weekday: day.weekday,
          purposeKey: entry.purposeKey,
          attendanceType: entry.attendanceType,
          availability: deriveAvailability(entry.purposeKey, purposes),
          startTime,
          endTime,
          details: entry.note || null,
          substituteCounselorId: entry.substituteCounselorId,
        },
      ];
    })
  );
}

type SeriesSubmitValues = Omit<WeeklySeriesFormValues, "dateFrom" | "dateTo"> & {
  dateFrom: Date;
  dateTo: Date;
};

function buildSeriesRequest(values: SeriesSubmitValues, purposes: SystemDropdownOptions): PlanWeeklySeriesRequest {
  return {
    dateFrom: values.dateFrom,
    dateTo: values.dateTo,
    originTimeZoneId: getLocalTimeZoneId(),
    entries: buildSeriesEntries(values.days, purposes),
  };
}

export function useWeeklySeriesForm(onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUserContext();
  const { purposes, purposeOptions, isLoading } = useAttendancePurposes();

  const planSeriesMutation = useMutation({
    mutationFn: (request: PlanWeeklyAttendanceSeriesRequest) => appointmentsApi.planWeeklyAttendanceSeries(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
      showSuccess(t("calendar.planAttendance.successMessage"));
      onClose();
    },
  });

  const formik = useFormikWithoutDirtyForm<WeeklySeriesFormValues>({
    initialValues: { dateFrom: null, dateTo: null, days: buildInitialEmptyDays() },
    validationSchema: weeklySeriesValidationSchema(t),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      if (!values.dateFrom || !values.dateTo) {
        return;
      }

      const { dateFrom, dateTo } = values;
      const endDateExclusive = addDays(dateTo, 1);
      const existing = await queryClient.query({
        queryKey: [
          QueryKey.Appointments,
          dateFrom.toISOString(),
          endDateExclusive.toISOString(),
          PurposeType.Attendance,
          currentUser.counselorId,
        ],
        queryFn: () =>
          appointmentsApi.getAppointmentsByDate({
            startDate: dateFrom,
            endDate: endDateExclusive,
            assignedCounselorId: currentUser.counselorId,
            purposeType: PurposeType.Attendance,
          }),
      });

      const entries = buildSeriesEntries(values.days, purposes);

      if (entries.length === 0) {
        showError(t("calendar.planAttendance.emptyWeekError"));
        return;
      }

      const entryWeekdays = new Set(entries.map((e) => e.weekday));
      const hasConflict = existing.some((a) => entryWeekdays.has(dateToDayOfWeek(a.startTimeUtc)));

      if (hasConflict) {
        const confirmed = await new Promise<boolean>((resolve) => {
          showConfirmationDialog({
            dialogTitleText: t("calendar.planAttendance.seriesConflictTitle"),
            dialogText: t("calendar.planAttendance.seriesConflictText"),
            agreeText: t("common.override"),
            agreeColor: "primary",
            discardText: t("common.cancel"),
            onClose: resolve,
          });
        });

        if (!confirmed) {
          return;
        }
      }

      await planSeriesMutation.mutateAsync({
        planWeeklySeriesRequest: buildSeriesRequest({ ...values, dateFrom, dateTo }, purposes),
      });
    },
  });

  const { setValues } = formik;

  const updateDayEntries = useCallback(
    async (dayIndex: number, updater: (entries: AttendanceEntryFormValues[]) => AttendanceEntryFormValues[]) => {
      await setValues((prev) => ({
        ...prev,
        days: prev.days.map((day, i) => (i === dayIndex ? { ...day, entries: updater(day.entries) } : day)),
      }));
    },
    [setValues]
  );

  const addEntry = useCallback(
    async (dayIndex: number) => updateDayEntries(dayIndex, (entries) => [...entries, createEmptyEntry()]),
    [updateDayEntries]
  );

  const removeEntry = useCallback(
    async (dayIndex: number, entryIndex: number) =>
      updateDayEntries(dayIndex, (entries) => entries.filter((_, i) => i !== entryIndex)),
    [updateDayEntries]
  );

  return {
    formik,
    purposes,
    purposeOptions,
    addEntry,
    removeEntry,
    updateDayEntries,
    isLoading,
  };
}
